import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import SystemService from "../services/SystemService";

export const LifecycleStagesContext = React.createContext();

const CYCLE_TYPES = ["negotiation", "mediation", "arbitration"];

const parseJsonArray = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  return [];
};

function reshapeStage(s) {
  if (!s) return null;
  const key = s.key || s.detailedStage || "";
  if (!key) return null;

  const label = s.label || s.caseStage || s.text || key;
  const text = s.text || label;
  const order = Number(s.order ?? s.stageIndex ?? s.index) || 0;
  const phaseIndex = Number(s.phaseIndex ?? s.index ?? order) || 0;

  let ctas = Array.isArray(s.ctas) ? [...s.ctas] : [];
  if (!ctas.length && s.ctaType && s.ctaType !== "none") {
    const CTA_META = {
      QUIT: {
        type: "QUIT",
        label: "Close Case",
        danger: true,
        displayBtn: true,
      },
      MEDIATION_SUCCESS: {
        type: "MEDIATION_SUCCESS",
        label: "Mediation success",
        danger: false,
        displayBtn: true,
      },
      REOPEN_CASE: {
        type: "REOPEN_CASE",
        label: "Reopen Case",
        danger: false,
        displayBtn: true,
      },
    };
    ctas = String(s.ctaType)
      .split(",")
      .map((t) => CTA_META[t.trim()])
      .filter(Boolean);
  }

  const resolutionKinds = Array.isArray(s.resolutionKinds)
    ? s.resolutionKinds
    : s.resolutionKind
      ? [s.resolutionKind]
      : [];

  const portals = Array.isArray(s.portals)
    ? s.portals
    : parseJsonArray(s.portals);

  return {
    id: s.id,
    key,
    label,
    text,
    detailedStagesLabel: s.detailedStagesLabel || "",
    detailedStage: s.detailedStage || "",
    index: phaseIndex,
    order,
    phaseIndex,
    enableSelection: !!s.enableSelection,
    cta: ctas,
    ctas,
    resolutionKinds,
    portals, // ← important
    caseLifeCycleDisplayFlag: s.caseLifeCycleDisplayFlag !== false,
    commentDisabled: !!s.commentDisabled,
    commentExpanded: !!s.commentExpanded,
    showAgreement: !!s.showAgreement,
    hideAddComment: !!s.hideAddComment,
    actionsDisabled: !!s.actionsDisabled,
    isQuitState: !!s.isQuitState,
    errorText: s.errorText || null,
    status: s.status || null,
    portalOverrides: s.portalOverrides || {},
    businessRules: s.businessRules || [],
    isActive: s.isActive !== false,
    role: s.role,
  };
}

function reshapeStages(rows = []) {
  return (rows || [])
    .map(reshapeStage)
    .filter((s) => s && s.key && s.isActive !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

function filterByResolutionKind(rows, resolutionKind) {
  if (!resolutionKind) return rows || [];
  const kind = String(resolutionKind).toLowerCase().trim();

  if (kind === "claimant") {
    return (rows || []).filter((s) => {
      const rk = s.resolutionKinds || [];
      if (!rk.length) return true;
      return (
        rk.includes("mediation") ||
        rk.includes("arbitration") ||
        rk.includes("negotiation")
      );
    });
  }

  return (rows || []).filter((s) => {
    const rk = s.resolutionKinds || [];
    if (!rk.length) return true;
    return rk.map((x) => String(x).toLowerCase()).includes(kind);
  });
}

function filterByPortal(stages, portalName) {
  if (!portalName) return stages || [];

  const portal = String(portalName).trim();

  return (stages || []).filter((stage) => {
    const allowed = stage.portals;
    if (!Array.isArray(allowed) || allowed.length === 0) return true;
    return allowed.includes(portal);
  });
}

function buildByType(reshaped, portalName = "Cm") {
  const byType = {};
  CYCLE_TYPES.forEach((kind) => {
    byType[kind] = filterByPortal(
      filterByResolutionKind(reshaped, kind),
      portalName,
    ).filter((s) => s.caseLifeCycleDisplayFlag);
  });

  byType.claimant = filterByPortal(
    filterByResolutionKind(reshaped, "claimant"),
    portalName,
  ).filter((s) => s.caseLifeCycleDisplayFlag);

  return byType;
}

export function buildLifecycle(rows, resolutionKind, portalName = "Cm") {
  if (!resolutionKind) return [];
  const kind = String(resolutionKind).toLowerCase().trim();

  const list = filterByPortal(
    filterByResolutionKind(rows || [], kind),
    portalName,
  ).filter((s) => s.caseLifeCycleDisplayFlag);

  return list.map((s) => {
    const cfg = resolveStageConfig(s, portalName, {});
    return {
      label: s.label,
      key: s.key,
      index: s.phaseIndex ?? s.index,
      enableSelection: !!s.enableSelection,
      text: s.detailedStagesLabel || s.label,
      cta: cfg.ctas || [],
    };
  });
}

async function loadAllStages() {
  const res = await SystemService.getCaseStages();
  if (Array.isArray(res)) return res;
  return res?.caseStages || res?.data || [];
}

function resolveStageConfig(stage, portalName, caseDetails = {}, userRole) {
  if (!stage) return {};

  let config = {
    key: stage.detailedStage,
    label: stage.label,
    text: stage.text,
    detailedStagesLabel: stage.detailedStagesLabel,
    detailedStage: stage.detailedStage,
    resolutionKinds: stage.resolutionKinds || [],
    portals: stage.portals || [],
    order: stage.order,
    phaseIndex: stage.phaseIndex,
    index: stage.index,
    caseLifeCycleDisplayFlag: !!stage.caseLifeCycleDisplayFlag,
    enableSelection: !!stage.enableSelection,
    commentDisabled: !!stage.commentDisabled,
    commentExpanded: !!stage.commentExpanded,
    hideAddComment: !!stage.hideAddComment,
    showAgreement: !!stage.showAgreement,
    actionsDisabled: !!stage.actionsDisabled,
    isQuitState: !!stage.isQuitState,
    status: stage.status || null,
    errorText: stage.errorText || null,
    ctas: Array.isArray(stage.ctas)
      ? [...stage.ctas]
      : Array.isArray(stage.cta)
        ? [...stage.cta]
        : [],
    isActive: stage.isActive !== false,
  };

  const portalOverride =
    (stage.portalOverrides && stage.portalOverrides[portalName]) || {};

  Object.keys(portalOverride).forEach((k) => {
    if (k === "clearCtas") {
      if (portalOverride.clearCtas) config.ctas = [];
    } else if (k === "ctas") {
      if (!portalOverride.clearCtas) config.ctas = portalOverride.ctas || [];
    } else {
      config[k] = portalOverride[k];
    }
  });

  parseJsonArray(stage.businessRules).forEach((rule) => {
    if (!rule?.when) return;

    if (String(caseDetails?.[rule.when.field]) === String(rule.when.equals)) {
      Object.assign(config, rule.then || {});
    }
  });

  config.ctas = (config.ctas || []).filter((cta) => {
    if (cta.displayBtn === false) return false;
    if (portalName && cta.portals?.length && !cta.portals.includes(portalName))
      return false;
    if (userRole && cta.roles?.length && !cta.roles.includes(userRole))
      return false;
    return true;
  });

  config.cta = config.ctas;
  return config;
}

function LifecycleStagesProvider({ children }) {
  const [allStages, setAllStages] = useState([]);
  const [stagesByType, setStagesByType] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const allStagesRef = useRef([]);
  const stagesByTypeRef = useRef({});
  const loadedRef = useRef(false);
  const inflightRef = useRef(null);

  const ensureLoaded = useCallback(async (force = false) => {
    if (!force && loadedRef.current && allStagesRef.current.length) {
      return allStagesRef.current;
    }
    if (!force && inflightRef.current) return inflightRef.current;

    const run = (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await loadAllStages();
        const reshaped = reshapeStages(rows);
        const byType = buildByType(reshaped, "Cm");
        allStagesRef.current = reshaped;
        stagesByTypeRef.current = byType;
        loadedRef.current = true;
        setAllStages(reshaped);
        setStagesByType(byType);
        setLoaded(true);
        return reshaped;
      } catch (err) {
        console.error("[LifecycleStages] load failed", err);
        setError(err);
        return allStagesRef.current;
      } finally {
        setLoading(false);
        if (inflightRef.current === run) inflightRef.current = null;
      }
    })();

    inflightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  const fetchStages = useCallback(
    async (cycleType, portalName = "Cm", force = false) => {
      if (!cycleType) return [];
      const kind = String(cycleType).toLowerCase().trim();

      const cacheKey = `${kind}_${portalName}`;

      if (!force && stagesByTypeRef.current[cacheKey]?.length) {
        return stagesByTypeRef.current[cacheKey];
      }

      const rows = await ensureLoaded(force);

      const filtered = filterByPortal(
        filterByResolutionKind(rows, kind),
        portalName,
      ).filter((s) => s.caseLifeCycleDisplayFlag);

      stagesByTypeRef.current = {
        ...stagesByTypeRef.current,
        [cacheKey]: filtered,
      };

      setStagesByType((prev) => ({ ...prev, [cacheKey]: filtered }));
      return filtered;
    },
    [ensureLoaded],
  );

  const getStages = useCallback((cycleType, portalName = "Cm") => {
    const source = allStagesRef.current;

    if (!cycleType) {
      return filterByPortal(source, portalName).filter(
        (s) => s.caseLifeCycleDisplayFlag,
      );
    }

    const kind = String(cycleType).toLowerCase().trim();
    const cacheKey = `${kind}_${portalName}`;

    if (stagesByTypeRef.current[cacheKey]) {
      return stagesByTypeRef.current[cacheKey];
    }

    if (!source.length) return [];

    return filterByPortal(
      filterByResolutionKind(source, kind),
      portalName,
    ).filter((s) => s.caseLifeCycleDisplayFlag);
  }, []);

  /**
   * @param {string} key
   * @param {object} [caseDetails]
   * @param {string} [portalName]
   * @param {string} [userRole]
   */
  const getStageConfig = useCallback(
    (key, caseDetails = {}, portalName = "Cm", userRole) => {
      if (!key) return {};
      const stage =
        allStagesRef.current.find((s) => s.key === key) ||
        Object.values(stagesByTypeRef.current)
          .flat()
          .find((s) => s.key === key);

      if (!stage) return { key };
      return resolveStageConfig(stage, portalName, caseDetails, userRole);
    },
    [],
  );

  /**
   * @param {string} resolutionKind
   * @param {string} [portalName]
   * @param {Array} [rowsOverride]
   */
  const getLifecycleForResolution = useCallback(
    (resolutionKind, portalName = "Cm", rowsOverride) => {
      if (!resolutionKind) return [];
      const source =
        Array.isArray(rowsOverride) && rowsOverride.length
          ? rowsOverride
          : allStagesRef.current;

      return buildLifecycle(source, resolutionKind, portalName);
    },
    [],
  );

  const value = useMemo(
    () => ({
      stagesByType,
      allStages,
      loading,
      error,
      loaded,
      fetchStages,
      getStages,
      getStageConfig,
      getLifecycleForResolution,
      ensureLoaded,
      buildLifecycle,
    }),
    [
      stagesByType,
      allStages,
      loading,
      error,
      loaded,
      fetchStages,
      getStages,
      getStageConfig,
      getLifecycleForResolution,
      ensureLoaded,
    ],
  );

  return (
    <LifecycleStagesContext.Provider value={value}>
      {children}
    </LifecycleStagesContext.Provider>
  );
}

export default LifecycleStagesProvider;
